import { useMemo } from 'react';
import { AgCharts } from 'ag-charts-react';

interface BudgetVsSpendingChartProps {
  data: Array<{
    brgId: number;
    brgName: string;
    allocated: number;
    spent: number;
  }>;
}

export function BudgetVsSpendingChart({ data }: BudgetVsSpendingChartProps) {
  const chartOptions = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Calculate reasonable max value: use max of actual data, but cap at reasonable limit
    // to avoid extreme outliers dominating the chart
    let maxValue = 0;
    data.forEach(item => {
      maxValue = Math.max(maxValue, item.allocated, item.spent);
    });

    // If there's an extreme outlier (more than 10x the median), cap it
    const sortedValues = data
      .flatMap(item => [item.allocated, item.spent])
      .filter(v => v > 0)
      .sort((a, b) => a - b);
    
    if (sortedValues.length > 0) {
      const median = sortedValues[Math.floor(sortedValues.length / 2)];
      if (maxValue > median * 10) {
        // There's likely an outlier; cap the max to be 5x the median
        maxValue = median * 5;
      }
    }

    return {
      data: data.map((item) => ({
        brg: item.brgName,
        allocated: item.allocated,
        spent: item.spent,
      })),
      title: {
        text: 'Budget vs. Spending by BRG',
        fontSize: 14,
        fontWeight: 600,
        color: '#333',
      },
      width: 1000,
      height: 300,
      legend: {
        position:'top' as const,
        item: {
          label: {
            fontSize: 12,
          },
        },
      },
      series: [
        {
          type: 'bar' as const,
          xKey: 'brg',
          yKey: 'allocated',
          yName: 'Allocated Budget',
          fill: '#1a3bcc',
        },
        {
          type: 'bar' as const,
          xKey: 'brg',
          yKey: 'spent',
          yName: 'Amount Spent',
          fill: '#7b9de0',
        },
      ],
      axes: [
        {
          type: 'category' as const,
          position: 'bottom' as const,
          label: {
            fontSize: 11,
          },
        },
        {
          type: 'number' as const,
          position: 'left' as const,
          min: 0,
          max: maxValue > 0 ? maxValue : 1000,
          label: {
            fontSize: 11,
            formatter: (params: { value: number }) => {
              if (params.value >= 1000) {
                return `$${(params.value / 1000).toFixed(0)}K`;
              }
              return `$${params.value}`;
            },
          },
        },
      ],
    };
  }, [data]);

  if (!chartOptions) {
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '6px',
          padding: '40px 20px',
          marginBottom: '18px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '13px', fontWeight: '700', color: '#333', marginBottom: '12px' }}>
          Budget vs. Spending by BRG
        </div>
        <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
          No data to display
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        padding: '16px 20px',
        marginBottom: '18px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <AgCharts options={chartOptions} />
      </div>
    </div>
  );
}
